import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { useState } from "react";
import { Toaster } from "sonner";
import App from "./App";
import { trpc } from "./lib/trpc";
import { createQueryClient, createTRPCClient } from "./lib/trpc-client";
import { PinProvider } from "./components/pin/PinProvider";
import { AuthProvider } from "./lib/auth";
import { ConfirmProvider } from "./lib/confirm";
import { lembrarEstacaoDeEtiquetas } from "./lib/estacao-de-etiquetas";
import { ThemeProvider } from "./lib/theme";
import { initSentry } from "./lib/sentry";
import { captureConnectInviteParam } from "./lib/pending-connect-invite";
import { capturarTokenDoVerComo } from "./lib/ver-como";
import "./styles/globals.css";

initSentry();
// Antes de qualquer navegação do react-router rodar (que pode descartar a query string) — captura
// o token de convite de conexão pendente, se veio um na URL.
captureConnectInviteParam();
capturarTokenDoVerComo();

function Root() {
  const [queryClient] = useState(() => createQueryClient());
  const [trpcClient] = useState(() => createTRPCClient());

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ThemeProvider>
            <AuthProvider>
              <ConfirmProvider>
                <PinProvider>
                  <App />
                </PinProvider>
                <Toaster
                  position="top-right"
                  closeButton
                  duration={1800}
                  visibleToasts={3}
                  // Diálogo aberto põe `pointer-events: none` no body e o aviso herdava: o
                  // "Desfazer" de abrir um item de outra organização ficava inclicável por cima
                  // do diálogo do próprio item.
                  style={{ pointerEvents: "auto" }}
                  toastOptions={{
                    classNames: {
                      toast:
                        "group bd-toast !rounded-xl !border !border-border !bg-card !shadow-xl !p-4 !gap-3",
                      title: "!text-sm !font-semibold !text-foreground",
                      description: "!text-xs !text-muted-foreground",
                      closeButton:
                        "!left-auto !right-2 !top-2 !translate-x-0 !translate-y-0 !border-border !bg-card !text-muted-foreground hover:!bg-muted hover:!text-foreground",
                      icon: "!mr-0",
                      success:
                        "![&_[data-icon]]:text-emerald-600 ![&>[data-icon]]:bg-emerald-50 dark:![&_[data-icon]]:text-emerald-400 dark:![&>[data-icon]]:bg-emerald-500/10",
                      error:
                        "![&_[data-icon]]:text-red-600 ![&>[data-icon]]:bg-red-50 dark:![&_[data-icon]]:text-red-400 dark:![&>[data-icon]]:bg-red-500/10",
                      info: "![&_[data-icon]]:text-brand-600 ![&>[data-icon]]:bg-brand-50 dark:![&_[data-icon]]:text-brand-400 dark:![&>[data-icon]]:bg-brand-500/10",
                    },
                  }}
                />
              </ConfirmProvider>
            </AuthProvider>
          </ThemeProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </trpc.Provider>
  );
}

lembrarEstacaoDeEtiquetas();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
